/**
 * Sending a chat turn again by itself (CLE-565). A send that fails in transit
 * before any reply arrives goes again at once, then once more after 2
 * seconds, always under the same client turn id: the server's execution
 * fence claims that id before it bills or runs anything, so a copy that
 * reaches it after the first one replays the first one's result (or answers
 * `turn_in_progress`) and never runs or charges a second turn.
 */

/** The wait before each send after the first: at once, then 2 seconds. */
export const AUTO_RETRY_DELAYS_MS: readonly number[] = [0, 2000];

/** A gateway answering for an API that is restarting or unreachable. */
export const GATEWAY_STATUSES: readonly number[] = [502, 503, 504];

/** The chat's own words for a stream that ended before its reply. */
export const CONNECTION_ENDED_MESSAGE =
  'The connection ended before the reply completed.';

/**
 * The request never got an answer: the browser's own `TypeError` ("Failed to
 * fetch", "Load failed", a reader's "network error"), or a stream that ended
 * before its first byte. A Stop (`AbortError`) never counts, nor does
 * anything the server wrote.
 */
export function isConnectionFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError') return false;
  return (
    error instanceof TypeError || error.message === CONNECTION_ENDED_MESSAGE
  );
}

/** Waits `ms`, ending early when `signal` aborts (a Stop). */
export function waitBeforeRetry(
  ms: number,
  signal: AbortSignal
): Promise<void> {
  if (ms <= 0 || signal.aborted) return Promise.resolve();
  return new Promise((done) => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      done();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener('abort', finish, { once: true });
  });
}
